import { useEffect } from 'react'
import dayjs from 'dayjs'
import { Field, reduxForm, reset } from 'redux-form'

import { exactly, required, valid, validator } from '../lib/validators'
import { ContinueButton } from '../shared/ContinueButton'
import { Help } from '../shared/Help'
import { SpinnerButton } from '../shared/SpinnerButton'
import {
  ButtonContainer,
  Card,
  CardHeader,
  PageInformation,
  Textbox
} from '../shared/styledComponents'
import { DateOfLossPicker } from './DatePicker'
import { ShowHelpInputWrapper } from './HelpInputWrapper'
import type { ClaimFormType } from './types'

const normalizeClaimNumber = (value: string) =>
  value && value.replace(/[^a-zA-Z\d]/g, '').toUpperCase().slice(0, 7)
const renderInput = ShowHelpInputWrapper(Textbox, Help)

const helpHeaderText = 'Where can I find my claim number?'

const claimHelp =
  <div>
    <p>
      Your claim number can be found in the documentation sent by Travelers. It is 7 characters
      consisting of letters and numbers.
    </p>
    Examples:
    <ul>
      <li>ABC1234</li>
      <li>123-CB-<mark>ABC1234</mark>-T </li>
    </ul>
  </div>


type ClaimFormProps = {
  disabled: boolean
  handleSubmit: (event: React.SyntheticEvent) => void
  requesting: boolean
}

const ClaimFormComponent = (props: ClaimFormProps) => {
  const { disabled, handleSubmit, requesting } = props

  useEffect(() => {
    reset('claimForm')
  }, [])

  return (
    <form autoComplete="off" onSubmit={handleSubmit} noValidate>
      <Card>
        <CardHeader>Find Claim</CardHeader>
        <PageInformation>
          {disabled
            ? 'Click "Continue" to proceed to the next screen.'
            : 'Please enter the claim number and date of loss so we can locate the correct claim.'
          }
        </PageInformation>
        <Field
          id="claimNumber"
          name="claimNumber"
          label="Claim Number"
          autoCapitalize="true"
          required={false}
          autoFocus={true} // eslint-disable-line jsx-a11y/no-autofocus
          type="text"
          normalize={normalizeClaimNumber}
          component={renderInput}
          helpText={claimHelp}
          helpHeader={helpHeaderText}
          disabled={disabled}
        />
        <br />
        <Field
          label="Date of Loss"
          id="dateOfLoss"
          name="dateOfLoss"
          component={DateOfLossPicker}
          disabled={disabled}
          required={!disabled}
        />
      </Card>
      <ButtonContainer>
        {requesting ? (
          <SpinnerButton />
        ) : <ContinueButton type="submit" disabled={false} />}
      </ButtonContainer>
    </form>
  )

}
const claimMessage = 'Please enter a valid claim number.'
const dateMessage = 'Please enter a valid date of loss.'

const exactly7 = exactly(7, claimMessage)
const allowOnlyLetterAndNumber = valid(/^[0-9a-zA-Z]+$/, claimMessage)
const notEmptyWithLetterAndNumber =
  validator(required(claimMessage), exactly7, allowOnlyLetterAndNumber)

const exactly10date = exactly(10, dateMessage)
const noFutureDate = (value: string) =>
  dayjs(value, 'MM/DD/YYYY').isAfter(dayjs(dayjs(), 'MM/DD/YYYY'))
    ? dateMessage
    : undefined


const validDate = (value: string) =>
  dayjs(value, 'MM/DD/YYYY').isValid()
    ? undefined
    : dateMessage

const notEmptyValidAndCurrentDate =
  validator(required(dateMessage), exactly10date, validDate, noFutureDate)

const validate = (values: ClaimFormType) => ({
  claimNumber: notEmptyWithLetterAndNumber(values.claimNumber),
  dateOfLoss: notEmptyValidAndCurrentDate(values.dateOfLoss)
})

const ClaimForm = reduxForm<ClaimFormType, ClaimFormProps>({
  form: 'claimForm',
  validate,
  touchOnChange: true,
  touchOnBlur: false
})(ClaimFormComponent)

export {
  ClaimForm
}
